import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useNoIndex } from "../hooks/useNoIndex";
import { Users, TrendingUp, Mail, CreditCard, MessageSquare, Crown, Activity, RefreshCw, Lock, Eye, EyeOff, Trash2 } from "lucide-react";
import { Sparkline } from "../components/Sparkline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "nihongo-admin-key";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtEur = (n) => `${(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;

const Stat = ({ icon: Icon, label, value, sub, color = "#1A2A40" }) => (
  <div className="bg-white border-2 border-[#1A2A40]/10 rounded-2xl p-5 hover:border-[#E94A3F]/30 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">{label}</div>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <div className="font-display font-black text-3xl text-[#1A2A40] mb-1">{value}</div>
    {sub && <div className="text-xs text-[#4A5568]">{sub}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-4 tracking-tight">{title}</h2>
    {children}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    lifetime: { label: "Lifetime", c: "bg-[#FFD166] text-[#1A2A40]" },
    paid: { label: "Premium", c: "bg-[#E94A3F] text-white" },
    trial: { label: "Trial", c: "bg-[#8CA07A] text-white" },
    expired: { label: "Expiré", c: "bg-[#1A2A40]/10 text-[#1A2A40]" },
  };
  const s = map[status] || map.expired;
  return <span className={`${s.c} px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest`}>{s.label}</span>;
};

const KeyGate = ({ onSubmit, error }) => {
  const [k, setK] = useState("");
  const [show, setShow] = useState(false);
  return (
    <div className="washi-bg min-h-screen flex items-center justify-center p-6">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(k.trim()); }}
        className="bg-white border-2 border-[#1A2A40] rounded-3xl p-10 max-w-md w-full shadow-brut"
        data-testid="admin-gate"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#1A2A40] rounded-2xl flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#FFD166]" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#A0AEC0]">Admin</div>
            <h1 className="font-display font-black text-2xl text-[#1A2A40]">Dashboard</h1>
          </div>
        </div>
        <label className="block text-xs font-bold uppercase tracking-widest text-[#4A5568] mb-2">Clé admin</label>
        <div className="relative mb-1">
          <input
            type={show ? "text" : "password"}
            value={k}
            onChange={(e) => setK(e.target.value)}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder="Entre ta clé secrète"
            className="w-full border-2 border-[#1A2A40]/20 rounded-xl px-4 py-3 pr-12 font-mono text-sm focus:border-[#E94A3F] outline-none"
            data-testid="admin-key-input"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#E94A3F]"
            aria-label={show ? "Masquer la clé" : "Afficher la clé"}
            data-testid="admin-key-toggle"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="text-[10px] text-[#A0AEC0] mb-3 font-mono">
          {k.length} caractère{k.length > 1 ? "s" : ""}
        </div>
        {error && (
          <div
            className="text-sm bg-[#E94A3F]/10 border border-[#E94A3F]/30 text-[#E94A3F] rounded-lg px-3 py-2 mb-4 font-medium"
            data-testid="admin-error"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          className="w-full bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3 rounded-xl shadow-brut-sm"
          data-testid="admin-submit"
        >
          Accéder au dashboard
        </button>
        <div className="mt-4 text-[11px] text-[#A0AEC0] text-center space-y-1">
          <div>
            Tu peux aussi accéder via{" "}
            <code className="bg-[#FDFBF7] px-1.5 py-0.5 rounded text-[10px]">/admin?key=…</code>
          </div>
          <div className="font-mono opacity-60 break-all">
            API : {process.env.REACT_APP_BACKEND_URL}
          </div>
        </div>
      </form>
    </div>
  );
};

const AdminDashboard = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const urlKey = searchParams.get("key");
  const [adminKey, setAdminKey] = useState(() => urlKey || localStorage.getItem(KEY_STORAGE) || "");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchStats = useCallback(async (rawKey) => {
    const k = (rawKey || "").trim();
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/stats`, { params: { key: k } });
      setStats(r.data);
      localStorage.setItem(KEY_STORAGE, k);
      setAdminKey(k);
    } catch (e) {
      // Build the most informative error message possible
      let msg;
      if (e.response) {
        const status = e.response.status;
        const detail = e.response.data?.detail;
        if (status === 503) {
          msg = "ADMIN_KEY n'est pas configurée sur le serveur. Ajoute ADMIN_KEY=… dans les variables d'environnement de prod puis redéploie.";
        } else if (status === 403) {
          msg = `Clé refusée (403). Vérifie qu'il n'y a pas d'espace en début/fin. Tu as tapé ${k.length} caractères.`;
        } else if (status === 422) {
          msg = "Requête invalide — la clé manque dans la query.";
        } else if (typeof detail === "string") {
          msg = `${status} — ${detail}`;
        } else {
          msg = `Erreur HTTP ${status}`;
        }
      } else if (e.request) {
        msg = "Le serveur n'a pas répondu (réseau / CORS / endpoint absent). Vérifie que le backend a été redéployé après l'ajout de la route /api/admin/stats.";
      } else {
        msg = e.message || "Erreur inconnue";
      }
      setError(msg);
      if (e.response?.status === 403) {
        localStorage.removeItem(KEY_STORAGE);
        setAdminKey("");
        setStats(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminKey) fetchStats(adminKey);
  }, [adminKey, fetchStats, refreshTick]);

  // Auto-refresh every 60s while focused
  useEffect(() => {
    const id = setInterval(() => setRefreshTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  if (!adminKey || (!stats && error)) {
    return <KeyGate onSubmit={fetchStats} error={error} />;
  }
  if (!stats) {
    return (
      <div className="washi-bg min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="text-[#4A5568]">Chargement…</div>
      </div>
    );
  }

  const u = stats.users;
  const g = stats.growth;
  const l = stats.leads;
  const p = stats.payments;
  const ai = stats.ai;

  return (
    <div className="washi-bg min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#E94A3F] mb-2">Admin · Read-only</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl text-[#1A2A40] leading-[1] tracking-tighter">Dashboard.</h1>
            <p className="text-sm text-[#4A5568] mt-2">
              Dernière mise à jour : {fmtDate(stats.generated_at)} · auto-refresh toutes les 60s
            </p>
          </div>
          <button
            onClick={() => setRefreshTick((t) => t + 1)}
            disabled={loading}
            data-testid="admin-refresh"
            className="bg-white border-2 border-[#1A2A40]/20 hover:border-[#E94A3F] rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </button>
        </div>

        {/* Test users banner (only if any present) */}
        {stats.test_users_count > 0 && (
          <div
            className="mb-8 bg-[#FFD166]/20 border-2 border-[#FFD166] rounded-2xl p-4 flex items-center justify-between gap-4"
            data-testid="admin-test-users-banner"
          >
            <div className="text-sm text-[#1A2A40]">
              <span className="font-bold">{stats.test_users_count} compte{stats.test_users_count > 1 ? "s" : ""} de test</span>{" "}
              détecté{stats.test_users_count > 1 ? "s" : ""} (e2e_, test-, smoke-, etc.) — exclu{stats.test_users_count > 1 ? "s" : ""} des
              statistiques mais encore présent{stats.test_users_count > 1 ? "s" : ""} dans la base.
            </div>
            <button
              onClick={async () => {
                if (!window.confirm(`Supprimer définitivement ${stats.test_users_count} compte(s) de test et leurs données associées (SRS, chat, leads) ? Action irréversible.`)) return;
                try {
                  const r = await axios.post(`${API}/admin/cleanup-test-users`, null, { params: { key: adminKey } });
                  alert(`✅ Supprimé : ${r.data.deleted_users} comptes, ${r.data.deleted_srs_cards} cartes SRS, ${r.data.deleted_chat_messages} messages, ${r.data.deleted_test_leads} leads.`);
                  setRefreshTick((t) => t + 1);
                } catch (e) {
                  alert("Erreur : " + (e.response?.data?.detail || e.message));
                }
              }}
              data-testid="admin-cleanup-test-users"
              className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-2 px-4 rounded-xl inline-flex items-center gap-2 text-sm whitespace-nowrap"
            >
              <Trash2 className="w-4 h-4" /> Nettoyer
            </button>
          </div>
        )}

        {/* Users */}
        <Section title="👥 Utilisateurs">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat icon={Users} label="Total" value={u.total} />
            <Stat icon={Crown} label="Lifetime" value={u.lifetime} color="#FFD166" />
            <Stat icon={Crown} label="Premium actif" value={u.active_premium} color="#E94A3F" />
            <Stat icon={Activity} label="En trial" value={u.trial_active} color="#8CA07A" />
            <Stat icon={Users} label="Expirés" value={u.expired} sub="Trial fini, pas payé" />
            <Stat icon={Mail} label="Emails vérifiés" value={`${u.verification_rate}%`} sub={`${u.email_verified}/${u.total}`} />
          </div>
        </Section>

        {/* Daily evolution sparkline (30 days) */}
        {stats.daily_30d?.length > 0 && (
          <Section title="📊 Évolution sur 30 jours">
            <Sparkline data={stats.daily_30d} />
          </Section>
        )}

        {/* Growth */}
        <Section title="📈 Croissance">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={TrendingUp} label="Inscrits aujourd'hui" value={g.today} color="#E94A3F" />
            <Stat icon={TrendingUp} label="Cette semaine" value={g.this_week} />
            <Stat icon={TrendingUp} label="Ce mois" value={g.this_month} />
            <Stat icon={CreditCard} label="Conversions / 7j" value={g.conversions_this_week} sub="Trial → payé" color="#8CA07A" />
          </div>
        </Section>

        {/* Leads (PDF magnet) */}
        <Section title="📕 Lead Magnet PDF">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat icon={Mail} label="Leads total" value={l.total} />
            <Stat icon={Mail} label="Aujourd'hui" value={l.today} color="#E94A3F" />
            <Stat icon={Mail} label="Cette semaine" value={l.this_week} />
            <Stat icon={Activity} label="Convertis" value={l.converted} sub={`Taux : ${l.conversion_rate_pct}%`} color="#8CA07A" />
            <Stat icon={Mail} label="Téléchargements" value={l.pdf_downloads_total} sub="Total cumulé" />
          </div>
        </Section>

        {/* Payments */}
        <Section title="💳 Paiements (30 jours)">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat icon={CreditCard} label="Revenu 30j" value={fmtEur(p.revenue_30d_eur)} color="#E94A3F" />
            <Stat icon={CreditCard} label="Paiements 30j" value={p.count_30d} />
            <Stat icon={CreditCard} label="Tous paiements" value={p.count_all_time} sub="Depuis le début" />
            <Stat icon={Crown} label="Fondateur" value={`${p.founder_slots_used}/${p.founder_slots_total}`} sub={`${p.founder_slots_remaining} dispo`} color="#FFD166" />
          </div>
          {Object.keys(p.plan_breakdown).length > 0 && (
            <div className="bg-white border border-[#1A2A40]/10 rounded-2xl p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0] mb-3">Répartition par plan</div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(p.plan_breakdown).map(([plan, count]) => (
                  <div key={plan} className="bg-[#FDFBF7] border border-[#1A2A40]/10 rounded-xl px-4 py-2">
                    <div className="text-xs text-[#A0AEC0] uppercase tracking-wider">{plan}</div>
                    <div className="font-display font-black text-xl text-[#1A2A40]">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* AI */}
        <Section title="🤖 Tuteur IA (aujourd'hui)">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Stat icon={MessageSquare} label="Messages aujourd'hui" value={ai.messages_today} color="#E94A3F" />
            <div className="lg:col-span-2 bg-white border-2 border-[#1A2A40]/10 rounded-2xl p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0] mb-3">Top utilisateurs IA</div>
              {ai.top_users_today.length === 0 ? (
                <div className="text-sm text-[#4A5568]">Aucun message aujourd'hui</div>
              ) : (
                <div className="space-y-2">
                  {ai.top_users_today.map((tu) => (
                    <div key={tu.email} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-[#1A2A40] truncate">{tu.email}</span>
                      <span className="font-bold text-[#E94A3F]">{tu.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* Recent users */}
        <Section title="🆕 10 derniers inscrits">
          <div className="bg-white border-2 border-[#1A2A40]/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FDFBF7] border-b border-[#1A2A40]/10">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Email</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Nom</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Inscription</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Statut</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Email ✓</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_users.map((ru, i) => (
                  <tr key={i} className="border-t border-[#1A2A40]/5 hover:bg-[#FDFBF7]/50">
                    <td className="px-4 py-3 font-mono text-xs text-[#1A2A40] truncate max-w-xs">{ru.email}</td>
                    <td className="px-4 py-3 text-[#4A5568]">{ru.name}</td>
                    <td className="px-4 py-3 text-[#4A5568] text-xs">{fmtDate(ru.created_at)}</td>
                    <td className="px-4 py-3"><StatusPill status={ru._status} /></td>
                    <td className="px-4 py-3 text-center">{ru.email_verified ? "✓" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Recent payments */}
        <Section title="💰 10 derniers paiements">
          {stats.recent_payments.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-[#1A2A40]/15 rounded-2xl p-8 text-center text-[#4A5568]">
              Aucun paiement encore. Tu y arriveras 🌸
            </div>
          ) : (
            <div className="bg-white border-2 border-[#1A2A40]/10 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FDFBF7] border-b border-[#1A2A40]/10">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Email</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Plan</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Montant</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Promo</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_payments.map((tx, i) => (
                    <tr key={i} className="border-t border-[#1A2A40]/5 hover:bg-[#FDFBF7]/50">
                      <td className="px-4 py-3 font-mono text-xs text-[#1A2A40] truncate max-w-xs">{tx.user_email}</td>
                      <td className="px-4 py-3 text-[#4A5568]">{tx.plan_id}</td>
                      <td className="px-4 py-3 font-bold text-[#E94A3F]">{fmtEur(tx.amount)}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs">{tx.promo_code || "—"}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-xs">{fmtDate(tx.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Footer */}
        <div className="text-center text-xs text-[#A0AEC0] mt-12 pb-6">
          <button
            onClick={() => { localStorage.removeItem(KEY_STORAGE); setAdminKey(""); setStats(null); }}
            className="text-[#E94A3F] hover:underline"
            data-testid="admin-logout"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
